import { useState, useEffect, useCallback } from 'react';
import { getFollowingStatus, followProgram, unfollowProgram } from '../api/programs';

export const useFollowingStatus = (programId) => {
  const [isFollowing, setIsFollowing] = useState(false);
  const [followingData, setFollowingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch following status
  const fetchFollowingStatus = useCallback(async () => {
    if (!programId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await getFollowingStatus(programId);
      setIsFollowing(data.isFollowing || false);
      setFollowingData(data);
    } catch (err) {
      // If 404 or other error, assume not following
      setIsFollowing(false);
      setFollowingData(null);
      if (err.status !== 404) {
        setError(err);
      }
    } finally {
      setLoading(false);
    }
  }, [programId]);

  // Toggle follow status
  const toggleFollow = useCallback(async () => {
    if (!programId) return;

    try {
      setLoading(true);
      setError(null);
      
      let data;
      if (isFollowing) {
        data = await unfollowProgram(programId);
      } else {
        data = await followProgram(programId);
      }
      
      setIsFollowing(data.isFollowing);
      setFollowingData(data);
      
      return data;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [programId, isFollowing]);

  // Follow program
  const follow = useCallback(async (notes = '') => {
    if (!programId) return;

    try {
      setLoading(true);
      setError(null);
      
      const data = await followProgram(programId, { notes });
      setIsFollowing(data.isFollowing);
      setFollowingData(data);
      
      return data;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [programId]);

  // Unfollow program
  const unfollow = useCallback(async () => {
    if (!programId) return;

    try {
      setLoading(true);
      setError(null);
      
      const data = await unfollowProgram(programId);
      setIsFollowing(data.isFollowing);
      setFollowingData(data);
      
      return data;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [programId]);

  // Initial fetch
  useEffect(() => {
    fetchFollowingStatus();
  }, [fetchFollowingStatus]);

  return {
    isFollowing,
    followingData,
    loading,
    error,
    toggleFollow,
    follow,
    unfollow,
    refetch: fetchFollowingStatus,
    
    // Convenience getters
    followedAt: followingData?.followedAt,
    unfollowedAt: followingData?.unfollowedAt,
    membershipStatus: followingData?.membershipStatus,
    role: followingData?.role,
  };
};

export default useFollowingStatus;

